
// src/layout/SidebarLayout.tsx
import React, { ReactNode, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Topbar } from "./Topbar";
import { useAuth } from "../hooks/useAuth";
import { apiUrl } from "../config";

type SidebarLayoutProps = {
  children: ReactNode;
};

export function SidebarLayout({ children }: SidebarLayoutProps) {
  const { user } = useAuth();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [teamLoading, setTeamLoading] = useState(false);

  useEffect(() => {
    const playerId = user?.faceit?.playerId;
    if (!playerId) {
      setTeamId(null);
      return;
    }
    let mounted = true;
    setTeamLoading(true);
    fetch(apiUrl(`/api/faceit/players/${encodeURIComponent(playerId)}/team`))
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!mounted) return;
        const nextTeamId = ok && !j?.error ? j?.teamId : null;
        setTeamId(nextTeamId || null);
      })
      .catch(() => mounted && setTeamId(null))
      .finally(() => mounted && setTeamLoading(false));
    return () => {
      mounted = false;
    };
  }, [user?.faceit?.playerId]);

  const linkClasses = (isActive: boolean) =>
    `px-3 py-2 rounded-md font-medium transition-all ${
      isActive
        ? "text-[#4FC3F7] bg-[#1F242C] border-l-4 border-[#4FC3F7]"
        : "hover:text-[#4FC3F7]"
    }`;

  return (
    <div className="min-h-screen w-full bg-bg text-text flex">
      {/* SIDEBAR */}
      <aside className="w-64 bg-surface border-r border-border p-4">
        {/* Site Title */}
        <div className="px-3 py-2 mb-4 text-lg font-bold">AntiStrat</div>

        {/* OVERVIEW */}
        <div className="px-3 pb-2 text-xs uppercase tracking-wider text-muted">
          Overview
        </div>
        <nav className="flex flex-col gap-1 mb-4">
          <NavLink to="/" className={({ isActive }) => linkClasses(isActive)}>
            Home
          </NavLink>
          <NavLink
            to="/dashboard"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Team Dashboard
          </NavLink>
          <NavLink
            to="/pricing"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Plans
          </NavLink>
        </nav>

        {/* INTERNAL INTELLIGENCE */}
        <div className="px-3 pb-2 mt-4 text-xs uppercase tracking-wider text-muted">
          Internal Intelligence
        </div>
        <nav className="flex flex-col gap-1 mb-4">
          <NavLink
            to="/team-hub"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Team Hub
          </NavLink>
          <NavLink
            to="/stats"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Player Stats
          </NavLink>
          <NavLink
            to="/focus"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Focus Areas
          </NavLink>
          <NavLink
            to="/team-notes"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Team Notes
          </NavLink>
        </nav>

        {/* EXTERNAL INTELLIGENCE */}
        <div className="px-3 pb-2 mt-4 text-xs uppercase tracking-wider text-muted">
          External Intelligence
        </div>
        <nav className="flex flex-col gap-1 mb-4">
          <NavLink
            to="/opponent-scouting"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Opponent Scouting
          </NavLink>
          <NavLink
            to="/upcoming-matches"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Opponent Schedule
          </NavLink>
          <NavLink
            to={teamId ? `/team/${teamId}` : "/faceit/intel"}
            className={({ isActive }) => linkClasses(isActive)}
            onClick={(e) => {
              if (!teamId && teamLoading) {
                e.preventDefault();
              }
            }}
          >
            {teamLoading ? "Team Profiles (loading...)" : "Team Profiles"}
          </NavLink>
          <NavLink
            to="/match-prep-notes"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Match Prep Notes
          </NavLink>
        </nav>

        {/* FACEIT API (Playground) */}
        <div className="px-3 pb-2 mt-4 text-xs uppercase tracking-wider text-muted">
          FACEIT API (Playground)
        </div>
        <nav className="flex flex-col gap-1">
          {/* <NavLink
            to="/faceit/player"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Player by ID
          </NavLink>
          <NavLink
            to="/faceit/search"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Search Players
          </NavLink>
          <NavLink
            to="/faceit/team"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Team by ID
          </NavLink>
          <NavLink
            to="/faceit/match"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Match by ID
          </NavLink>
          <NavLink
            to="/faceit/match-stats"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Match Stats
          </NavLink>
          <NavLink
            to="/faceit/championships"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Championships
          </NavLink> */}
          <NavLink
            to="/faceit/intel"
            className={({ isActive }) => linkClasses(isActive)}
          >
            Opponent Intelligence
          </NavLink>
        </nav>
      </aside>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col">
        <Topbar />
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
